import { CardContent } from '@mui/material'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import React from 'react'

import { SchemaInvoice } from '@/__generated/es-api'
import TablePagination from '@/app/component/atom/table-pagination'
import Loading from '@/app/component/guard/loading'
import TableRowActions from '@/app/component/layout/table/actions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_DOWNLOAD_INVOICE_SERVER } from '@/app/module/payments/definitions'
import download from '@/app/service/util/download'
import { captureError } from '@/app/service/util/error'
import { getMonthName } from '@/app/service/util/format'
import { useAppDispatch } from '@/app/store/redux-hooks'

import DownloadInvoiceDialog from './download-invoice-dialog'

type InvoiceType = {
  year: number
  month: number
  date: string
  pdf: string
  xlsx: string
}

type Props = {
  invoices: InvoiceType[]
  isFetching: boolean
  orgId: number
  role: string
}

const pageSizeValues = [
  {
    label: '5',
    value: 5,
  },
  {
    label: '10',
    value: 10,
  },
  {
    label: '15',
    value: 15,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '50',
    value: 50,
  },
  {
    label: '100',
    value: 100,
  },
]

function Invoices({ invoices, isFetching, orgId, role }: Props) {
  const dispatch = useAppDispatch()
  const [isDownloading, setIsDownloading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(15)
  const [toBeDownloaded, setToBeDownloaded] = React.useState<SchemaInvoice | undefined>()

  const isAuthorized = React.useMemo(() => role === 'owner' || role === 'manager', [role])

  const downloadInvoiceXLSX = async (invoice: SchemaInvoice) => {
    setIsDownloading(true)
    try {
      await download.invoices(orgId, invoice.xlsx)
    } catch (e) {
      captureError(e)
      dispatch(
        createNotification({
          'download-invoice': {
            type: 'error',
            message: ERROR_DOWNLOAD_INVOICE_SERVER,
          },
        }),
      )
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
  }

  return (
    <Loading style={{ height: '100%' }} isLoading={isFetching || isDownloading}>
      <Container>
        <TableContainer>
          <Table size="small" stickyHeader={false}>
            <TableHead>
              <TableRow>
                <TableHeadCell>Year</TableHeadCell>
                <TableHeadCell align="left">Month</TableHeadCell>
                <TableHeadCell align="center">Actions</TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: InvoiceType) => (
                <TableRow key={row.date} hover>
                  <TableCell>{row.year}</TableCell>
                  <TableCell align="left">{getMonthName[row.month - 1]}</TableCell>
                  <TableCell align="center">
                    <ActionsContainer>
                      <TableRowActions
                        config={[
                          {
                            icon: 'picture_as_pdf',
                            label: 'Download PDF',
                            tooltip: isAuthorized
                              ? 'Download this Usage Statement as PDF'
                              : 'You are not authorized to download usage statements.',
                            disabled: !isAuthorized,
                            onClick: () => setToBeDownloaded(row),
                          },
                          {
                            icon: 'table_view',
                            label: 'Download XLSX',
                            tooltip: isAuthorized
                              ? 'Download this Usage Statement as XSLX'
                              : 'You are not authorized to download usage statements.',
                            disabled: !isAuthorized || isDownloading,
                            onClick: () => downloadInvoiceXLSX(row),
                          },
                        ]}
                      />
                    </ActionsContainer>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          namespace="Invoices"
          page={page + 1}
          size={rowsPerPage}
          total={invoices.length}
          pageSizeValues={pageSizeValues}
          onPageSizeChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
        />
      </Container>
      {!!toBeDownloaded && (
        <DownloadInvoiceDialog invoice={toBeDownloaded} orgId={orgId} onClose={() => setToBeDownloaded(undefined)} />
      )}
    </Loading>
  )
}

const Container = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const TableContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  overflow: 'auto',

  '& table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderTop: 'none',
  },
})

const TableHeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}))

const ActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  '& > div': {
    marginLeft: 0,
    marginRight: 0,
  },
})

export default Invoices
